/**
 * Export-time audio: mix the timeline's music and every audible video clip's
 * soundtrack into one AudioBuffer with an OfflineAudioContext (faster than
 * real time, sample-exact), then hand it to the encoder in <=1024-frame
 * AudioData chunks.
 *
 * Sources are decoded from the IndexedDB blob, so no CORS, ever. A missing or
 * undecodable music asset is NOT an error: the export proceeds without it and
 * the returned `warning` tells the user why. A clip that has no audio track
 * (or can't be decoded) is skipped quietly — silent clips are normal, and the
 * scene compositor already warns about clips whose media is missing.
 *
 * Chunks are sliced lazily as the export loop advances rather than all up
 * front: a 5-minute stereo mix is ~100 MB of float samples, and copying it
 * into AudioData objects in one go would double that.
 */

import type { AudioTrack } from '@canvas/shared-types';
import { audioWindow, effectiveFades, type AudioWindow } from './audioTrack';
import { abortError } from './exportErrors';
import { audioSampleCount, audioTimestampMicros, samplesBefore } from './frameTiming';
import { resolveMediaBlob, type MediaRef } from './videoAssetUpload';
import { clipAudible, clipLengthMs, type PlacedClip } from './videoClip';

/** Frames per AudioData; AAC encoders consume 1024-frame packets. */
export const AUDIO_CHUNK_FRAMES = 1024;

export interface RenderedAudio {
  sampleRate: number;
  numberOfChannels: number;
  buffer: AudioBuffer;
  durationMs: number;
}

export interface MixResult {
  audio: RenderedAudio | null;
  /** User-facing reason the export will be silent, when it will be. */
  warning?: string;
}

const MISSING_COPY =
  'Your music couldn’t be loaded (it may not have finished uploading from another device), ' +
  'so this video was exported without music. Re-import the track to fix it.';
const DECODE_COPY =
  'Your music file couldn’t be decoded in this browser, so this video was exported without music.';

/** Clip edges ramp over this long so a hard cut at a scene boundary doesn't click. */
const CLIP_EDGE_RAMP_SEC = 0.01;

async function decode(ctx: BaseAudioContext, blob: Blob): Promise<AudioBuffer | null> {
  try {
    return await ctx.decodeAudioData(await blob.arrayBuffer());
  } catch {
    return null;
  }
}

/** Schedule one clip's soundtrack inside its scene window. */
function scheduleClip(ctx: OfflineAudioContext, buffer: AudioBuffer, placed: PlacedClip, totalMs: number): void {
  const { clip } = placed;
  const start = placed.startMs / 1000;
  const sceneSec = (Math.min(placed.endMs, totalMs) - placed.startMs) / 1000;
  const lengthSec = clipLengthMs(clip) / 1000;
  // start()'s duration is in BUFFER time, so a 2x clip consumes 2 s of source per second.
  const sourceSec = clip.loop ? sceneSec * clip.rate : Math.min(lengthSec, sceneSec * clip.rate);
  const outSec = sourceSec / clip.rate;
  if (outSec <= 0) return;

  const node = ctx.createBufferSource();
  node.buffer = buffer;
  node.playbackRate.value = clip.rate;
  if (clip.loop) {
    node.loop = true;
    node.loopStart = clip.trimStartMs / 1000;
    node.loopEnd = clip.trimEndMs / 1000;
  }
  const gain = ctx.createGain();
  node.connect(gain).connect(ctx.destination);
  const ramp = Math.min(CLIP_EDGE_RAMP_SEC, outSec / 2);
  const g = gain.gain;
  g.setValueAtTime(0, start);
  g.linearRampToValueAtTime(clip.volume, start + ramp);
  g.setValueAtTime(clip.volume, start + outSec - ramp);
  g.linearRampToValueAtTime(0, start + outSec);
  node.start(start, clip.trimStartMs / 1000, sourceSec);
}

export async function renderTimelineAudio(args: {
  track?: AudioTrack | null;
  /** Extra music and voice clips. Each track's offsetMs is where it starts. */
  tracks?: AudioTrack[];
  /** Clip layers with their scene windows (see timelineClips). */
  clips?: PlacedClip[];
  durationMs: number;
  sampleRate: 44100 | 48000;
  channels: 2;
  signal?: AbortSignal;
  /** Injected for tests; defaults to the IDB-first resolver. */
  resolveBlob?: (media: MediaRef) => Promise<Blob | null>;
}): Promise<MixResult> {
  const { track, durationMs, sampleRate, channels, signal } = args;
  const resolve = args.resolveBlob ?? resolveMediaBlob;
  const tracks = [track, ...(args.tracks ?? [])].filter((item): item is AudioTrack => Boolean(item && item.volume > 0));
  const clips = (args.clips ?? []).filter((c) => clipAudible(c.clip) && c.startMs < durationMs);
  if (!tracks.length && !clips.length) return { audio: null };

  const length = audioSampleCount(durationMs, sampleRate);
  const ctx = new OfflineAudioContext(channels, length, sampleRate);
  let warning: string | undefined;
  let scheduled = 0;

  // One decode per source file, however many layers reuse it.
  const decoded = new Map<string, Promise<AudioBuffer | null>>();
  for (const placed of clips) {
    const key = placed.clip.idbKey || placed.clip.storagePath || placed.clip.clipId;
    let pending = decoded.get(key);
    if (!pending) {
      pending = resolve(placed.clip).then((blob) => (blob ? decode(ctx, blob) : null));
      decoded.set(key, pending);
    }
    const buffer = await pending;
    if (signal?.aborted) throw abortError();
    if (!buffer) continue;
    scheduleClip(ctx, buffer, placed, durationMs);
    scheduled += 1;
  }

  for (const item of tracks) {
    const w = audioWindow(item, durationMs);
    if (!w) continue;
    const blob = await resolve(item);
    if (signal?.aborted) throw abortError();
    const source = blob ? await decode(ctx, blob) : null;
    if (signal?.aborted) throw abortError();
    if (!source) {
      if (!warning) warning = blob ? DECODE_COPY : MISSING_COPY;
    } else {
      scheduleTrack(ctx, source, item, w);
      scheduled += 1;
    }
  }

  if (!scheduled) return { audio: null, warning };
  const buffer = await ctx.startRendering();
  if (signal?.aborted) throw abortError();
  return { audio: { sampleRate, numberOfChannels: channels, buffer, durationMs }, warning };
}

function scheduleTrack(ctx: OfflineAudioContext, source: AudioBuffer, track: AudioTrack, w: AudioWindow): void {
  const node = ctx.createBufferSource();
  node.buffer = source;
  const gain = ctx.createGain();
  node.connect(gain).connect(ctx.destination);

  const start = w.startMs / 1000;
  const end = w.endMs / 1000;
  const { inMs, outMs } = effectiveFades(track, w);
  const g = gain.gain;
  g.setValueAtTime(inMs > 0 ? 0 : track.volume, start);
  if (inMs > 0) g.linearRampToValueAtTime(track.volume, start + inMs / 1000);
  if (outMs > 0) {
    g.setValueAtTime(track.volume, end - outMs / 1000);
    g.linearRampToValueAtTime(0, end);
  }
  node.start(start, w.sourceStartMs / 1000, end - start);
}

export interface AudioChunkReader {
  /** AudioData for every not-yet-read sample stamped before `untilMicros`. Caller closes them. */
  take(untilMicros: number): AudioData[];
  readonly done: boolean;
}

export function createAudioChunkReader(audio: RenderedAudio): AudioChunkReader {
  const { buffer, sampleRate, numberOfChannels } = audio;
  const total = buffer.length;
  const planes = Array.from({ length: numberOfChannels }, (_, c) => buffer.getChannelData(c));
  let pos = 0;

  return {
    take(untilMicros) {
      const stop = Math.min(total, samplesBefore(untilMicros, sampleRate));
      const out: AudioData[] = [];
      while (pos < stop) {
        const frames = Math.min(AUDIO_CHUNK_FRAMES, total - pos);
        // f32-planar: each channel's frames back to back.
        const data = new Float32Array(frames * numberOfChannels);
        planes.forEach((plane, c) => data.set(plane.subarray(pos, pos + frames), c * frames));
        out.push(
          new AudioData({
            format: 'f32-planar',
            sampleRate,
            numberOfFrames: frames,
            numberOfChannels,
            timestamp: audioTimestampMicros(pos, sampleRate),
            data,
          })
        );
        pos += frames;
      }
      return out;
    },
    get done() {
      return pos >= total;
    },
  };
}
